import { useCallback, useEffect, useState } from "react";

import { fetchFastApi } from "../../api/fastapi";
import { UserSchema } from "../../models/user";

/**
 * `userId -> email` lookup for audit feeds.
 *
 * Worker-emitted events record only `triggeredByUserId` — `Users` is not
 * importable from `midship_core`, so the backend cannot denormalize the email
 * at write time and the client resolves it instead. Refetches whenever
 * `enabled` turns on (each drawer open), and stays quiet on failure:
 * an unresolved id degrades to "Unknown user" rather than a toast.
 */
export const useMemberEmailLookup = (
  enabled: boolean,
): ((userId: string) => string | undefined) => {
  const [emailByUserId, setEmailByUserId] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;

    const loadMembers = async () => {
      try {
        const data = await fetchFastApi({
          path: "/workspace/members",
          method: "GET",
        });
        // Per-item parse: one malformed member must degrade that one entry,
        // not blank actor attribution for every row in the feed.
        const rawUsers: unknown[] = Array.isArray(data.users) ? data.users : [];
        const users = rawUsers.flatMap((candidate) => {
          const result = UserSchema.safeParse(candidate);
          if (!result.success) {
            console.warn(
              "Dropping unrecognized workspace member",
              result.error,
            );
            return [];
          }
          return [result.data];
        });
        if (!cancelled) {
          setEmailByUserId(
            Object.fromEntries(users.map((user) => [user.id, user.email])),
          );
        }
      } catch (error) {
        console.error("Failed to resolve audit actor emails", error);
      }
    };

    loadMembers();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return useCallback(
    (userId: string) => emailByUserId[userId],
    [emailByUserId],
  );
};
